import { useState, useEffect, useRef } from 'react'
import adminFetch from './adminFetch'
import AdminShopDetail from './AdminShopDetail'
import AdminPaymentLink from './AdminPaymentLink'


// One shared helper now: see admin/adminFetch for why three copies of this
// was the bug, not just duplication.
const apiFetch = adminFetch

function timeAgo(iso) {
  if (!iso) return '—'
  const s = Math.floor((Date.now() - new Date(iso)) / 1000)
  if (s < 60)    return `${s}s ago`
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return `${Math.floor(s / 86400)}d ago`
}

function fmt(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const PLAN_COLORS = {
  starter:    '#6b7280',
  growth:     '#4f8ef7',
  pro:        '#a78bfa',
  enterprise: '#f5a623',
}

export default function AdminShops({ token, onLogout, onImpersonate, detailShop, onShowDetail, onCloseDetail }) {
  const [shops,       setShops]       = useState([])
  const [stats,       setStats]       = useState({})
  const [loading,     setLoading]     = useState(true)
  const [search,      setSearch]      = useState('')
  const [filter,      setFilter]      = useState('all')
  const [openMenu,    setOpenMenu]    = useState(null)
  const [paymentShop, setPaymentShop] = useState(null)
  // Which side-nav entry is showing. The panel had no navigation at all —
  // one page with the stats bolted above the seller table — so the numbers
  // and the list competed for the same screen and neither got it.
  const [view,        setView]        = useState('dashboard')
  const menuRef = useRef()

  useEffect(() => {
    load()
  }, [])

  // Close menu on outside click
  useEffect(() => {
    function handler(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenu(null)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  async function load() {
    setLoading(true)
    const [shopsData, statsData] = await Promise.all([
      apiFetch('/shops', token),
      apiFetch('/stats', token),
    ])
    setShops(shopsData.shops || [])
    setStats(statsData || {})
    setLoading(false)
  }

  async function triggerSync(shop) {
    setOpenMenu(null)
    await apiFetch(`/shops/${shop.id}/sync`, token, { method: 'POST' })
    alert(`Sync started for ${shop.shopify_domain}`)
  }

  async function testOrder(shop) {
    setOpenMenu(null)
    try {
      const data = await apiFetch(`/shops/${shop.id}/test-order`, token, { method: 'POST' })
      if (data.error) { alert(`Test order failed: ${data.error}`); return }
      alert(`✓ Test order created!\nProduct: ${data.product}\nReceipt ID: ${data.fakeReceiptId}\nStatus: ${data.result?.status}\n\nCheck Shopify Orders to see it.`)
    } catch (e) {
      alert(`Test order error: ${e.message}`)
    }
  }

  async function clearActivity(shop) {
    setOpenMenu(null)
    if (!window.confirm(`Clear all activity logs for ${shop.shopify_domain}?`)) return
    await apiFetch(`/shops/${shop.id}/activity`, token, { method: 'DELETE' })
    alert('Activity log cleared')
  }

  const filtered = shops.filter(s => {
    const matchSearch = !search ||
      s.shopify_domain.includes(search.toLowerCase()) ||
      (s.ebay_shop_name || '').toLowerCase().includes(search.toLowerCase())
    const matchFilter =
      filter === 'all'         ? true :
      filter === 'active'      ? s.is_active :
      filter === 'uninstalled' ? !s.is_active :
      filter === 'paying'      ? s.charge_status === 'active' :
      filter === 'trial'       ? (s.trial_ends_at && new Date(s.trial_ends_at) > new Date()) : true
    return matchSearch && matchFilter
  })

  return (
    <div style={{ minHeight: '100vh', background: '#0a0d14', color: '#f1f5f9', display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <div style={{
        background: '#111827', borderBottom: '1px solid #1f2937',
        padding: '0 28px', height: 58,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 34, height: 34, borderRadius: 9,
            background: 'linear-gradient(135deg, #79c142, #0064d2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 800, color: '#fff',
          }}>S↔E</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>SyncE Bridge</div>
            <div style={{ fontSize: 10, color: '#0064d2', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' }}>Admin Panel</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={load} style={{
            background: '#1f2937', border: '1px solid #374151', borderRadius: 8,
            color: '#9ca3af', padding: '7px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600,
          }}>↺ Refresh</button>
          <button onClick={onLogout} style={{
            background: 'rgba(0,100,210,0.1)', border: '1px solid rgba(0,100,210,0.25)',
            borderRadius: 8, color: '#0064d2', padding: '7px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600,
          }}>Sign out</button>
        </div>
      </div>

      <div style={{ display: 'flex', flex: 1, minHeight: 0, width: '100%', maxWidth: 1420, margin: '0 auto' }}>

        {/* ── Side navigation ──
            Staff tool, so it is plain: two destinations and no cleverness.
            Kept inside src/admin/ in its own dark palette — this is not the
            merchant app and shares none of its chrome. */}
        <nav aria-label="Admin sections" style={{
          width: 190, flexShrink: 0, borderRight: '1px solid #1f2937',
          padding: '22px 12px', display: 'flex', flexDirection: 'column', gap: 4,
        }}>
          {[
            ['dashboard', 'Dashboard', '▦'],
            ['shops',     'Sellers',   '▤'],
          ].map(([id, label, icon]) => (
            <button key={id} onClick={() => setView(id)}
              aria-current={view === id ? 'page' : undefined}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                padding: '9px 12px', borderRadius: 9, cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 13, textAlign: 'left',
                fontWeight: view === id ? 700 : 500,
                background: view === id ? '#1b2436' : 'transparent',
                border: '1px solid ' + (view === id ? '#2b3a55' : 'transparent'),
                color: view === id ? '#f1f5f9' : '#9aa5b5',
              }}>
              <span aria-hidden="true" style={{ fontSize: 14 }}>{icon}</span>
              {label}
            </button>
          ))}
        </nav>

      <div style={{ padding: '28px', width: '100%', minWidth: 0, flex: 1 }}>

        {/* Stats row */}
        {view === 'dashboard' && !loading && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 28 }}>
            {[
              { label: 'Total Sellers',   value: stats.total_shops,      color: '#4f8ef7' },
              { label: 'Active',          value: stats.active_shops,     color: '#34c97e' },
              { label: 'Uninstalled',     value: stats.uninstalled_shops, color: '#6b7280' },
              { label: 'Paying',          value: stats.paying_shops,     color: '#f5a623' },
              { label: 'On Trial',        value: stats.trial_shops,      color: '#a78bfa' },
            ].map(s => (
              <div key={s.label} style={{
                background: '#111827', border: '1px solid #1f2937', borderRadius: 14,
                padding: '18px 20px',
              }}>
                <div style={{ fontSize: 28, fontWeight: 900, color: s.color, marginBottom: 4, letterSpacing: '-1px' }}>
                  {s.value ?? '—'}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Catalogue, across every seller.
            /api/admin/stats has returned total_products and published all
            along and nothing rendered them, so the one question the panel
            exists to answer — how much is actually on eBay — could not be
            asked without opening a seller. */}
        {view === 'dashboard' && !loading && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 28 }}>
            {[
              { label: 'Products across all sellers', value: stats.total_products, color: '#4f8ef7' },
              { label: 'Published to eBay',           value: stats.published,      color: '#34c97e' },
            ].map(s => (
              <div key={s.label} style={{
                background: '#111827', border: '1px solid #1f2937', borderRadius: 14,
                padding: '18px 20px',
              }}>
                <div style={{ fontSize: 28, fontWeight: 900, color: s.color, marginBottom: 4, letterSpacing: '-1px' }}>
                  {s.value ?? '—'}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {view === 'dashboard' && (
          <div style={{ fontSize: 12.5, color: '#6b7280' }}>
            Open <strong style={{ color: '#9aa5b5' }}>Sellers</strong> to search, filter,
            or view a seller’s portal in a new tab.
          </div>
        )}

        {view === 'shops' && (<>
        {/* Search + filter */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by domain or eBay shop…"
            style={{
              background: '#111827', border: '1px solid #374151', borderRadius: 10,
              color: '#f1f5f9', padding: '9px 14px', fontSize: 13, outline: 'none',
              width: 280,
            }}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            {[['all','All'],['active','Active'],['uninstalled','Uninstalled'],['paying','Paying'],['trial','Trial']].map(([val, label]) => (
              <button key={val} onClick={() => setFilter(val)} style={{
                padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                background: filter === val ? '#0064d2' : '#1f2937',
                color: filter === val ? '#fff' : '#9ca3af',
                transition: 'all .15s',
              }}>{label}</button>
            ))}
          </div>
          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#4b5563' }}>
            {filtered.length} of {shops.length} sellers
          </span>
        </div>

        {/* Shops table */}
        <div style={{ background: '#111827', border: '1px solid #1f2937', borderRadius: 16, overflow: 'visible' }}>
          {/* Table header */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 120px 130px 110px 100px 90px 60px',
            padding: '12px 20px', borderBottom: '1px solid #1f2937',
            borderRadius: '16px 16px 0 0',
            fontSize: 11, fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '.06em',
          }}>
            <div>Seller</div>
            <div>Plan</div>
            <div>eBay Shop</div>
            <div>Products</div>
            <div>Status</div>
            <div>Installed</div>
            <div></div>
          </div>

          {loading && (
            <div style={{ padding: '40px', textAlign: 'center', color: '#4b5563', fontSize: 14 }}>
              Loading sellers…
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div style={{ padding: '40px', textAlign: 'center', color: '#4b5563', fontSize: 14 }}>
              No sellers found
            </div>
          )}

          {!loading && filtered.map((shop, i) => (
            <div key={shop.id} style={{
              display: 'grid', gridTemplateColumns: '1fr 120px 130px 110px 100px 90px 60px',
              padding: '14px 20px', alignItems: 'center',
              borderBottom: i < filtered.length - 1 ? '1px solid #1f2937' : 'none',
              background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)',
              transition: 'background .15s',
              position: 'relative',
            }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
              onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)'}
            >
              {/* Seller info */}
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9', marginBottom: 2 }}>
                  {shop.shopify_domain}
                </div>
                <div style={{ fontSize: 11, color: '#4b5563' }}>
                  {shop.last_activity ? `Last active ${timeAgo(shop.last_activity)}` : 'No activity'}
                </div>
              </div>

              {/* Plan */}
              <div>
                {shop.plan_name ? (
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 6,
                    background: `${PLAN_COLORS[shop.plan_name] || '#6b7280'}18`,
                    color: PLAN_COLORS[shop.plan_name] || '#6b7280',
                    border: `1px solid ${PLAN_COLORS[shop.plan_name] || '#6b7280'}30`,
                    textTransform: 'capitalize',
                  }}>{shop.plan_name}</span>
                ) : (
                  <span style={{ fontSize: 11, color: '#4b5563' }}>No plan</span>
                )}
              </div>

              {/* eBay shop */}
              <div style={{ fontSize: 12, color: shop.ebay_shop_name ? '#f5a623' : '#4b5563' }}>
                {shop.ebay_shop_name || '—'}
              </div>

              {/* Products / listings */}
              <div>
                <div style={{ fontSize: 12, color: '#f1f5f9' }}>{shop.product_count || 0} products</div>
                <div style={{ fontSize: 11, color: '#4b5563' }}>{shop.listing_count || 0} on eBay</div>
              </div>

              {/* Status badge */}
              <div>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '4px 9px', borderRadius: 6,
                  background: shop.is_active ? 'rgba(52,201,126,0.1)' : 'rgba(107,114,128,0.1)',
                  color: shop.is_active ? '#34c97e' : '#6b7280',
                  border: `1px solid ${shop.is_active ? 'rgba(52,201,126,0.2)' : 'rgba(107,114,128,0.2)'}`,
                }}>
                  {shop.is_active ? 'Active' : 'Uninstalled'}
                </span>
              </div>

              {/* Installed date */}
              <div style={{ fontSize: 11, color: '#6b7280' }}>{fmt(shop.installed_at)}</div>

              {/* Three dot menu */}
              <div style={{ position: 'relative' }} ref={openMenu === shop.id ? menuRef : null}>
                <button
                  onClick={e => { e.stopPropagation(); setOpenMenu(openMenu === shop.id ? null : shop.id) }}
                  style={{
                    background: 'none', border: '1px solid transparent', borderRadius: 6,
                    color: '#6b7280', cursor: 'pointer', padding: '4px 8px', fontSize: 16,
                    transition: 'all .15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#374151'; e.currentTarget.style.color = '#f1f5f9' }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.color = '#6b7280' }}
                >⋯</button>

                {openMenu === shop.id && (
                  <div style={{
                    position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 200,
                    background: '#1f2937', border: '1px solid #374151', borderRadius: 12,
                    overflow: 'hidden', minWidth: 200,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                  }}>
                    <MenuItem icon="👁" label="View seller portal" onClick={() => { setOpenMenu(null); onImpersonate(shop) }}/>
                    <MenuItem icon="📋" label="View details" onClick={() => { setOpenMenu(null); onShowDetail(shop) }}/>
                    {shop.is_active && <MenuItem icon="↺" label="Trigger sync" onClick={() => triggerSync(shop)} />}
                    {shop.is_active && <MenuItem icon="🧪" label="Test order sync" onClick={() => testOrder(shop)} />}
                    <div style={{ height: 1, background: '#374151', margin: '4px 0' }}/>
                    <MenuItem icon="💳" label="Create payment link" onClick={() => { setOpenMenu(null); setPaymentShop(shop) }} />
                    <div style={{ height: 1, background: '#374151', margin: '4px 0' }}/>
                    <MenuItem icon="🗑" label="Clear activity log" onClick={() => clearActivity(shop)} danger />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        </>)}
      </div>
      </div>

      {/* Detail panel */}
      {detailShop && (
        <AdminShopDetail
          shop={detailShop}
          token={token}
          onClose={onCloseDetail}
          onImpersonate={() => { onCloseDetail(); onImpersonate(detailShop) }}
        />
      )}

      {/* Payment link panel */}
      {paymentShop && (
        <AdminPaymentLink
          shop={paymentShop}
          token={token}
          onClose={() => setPaymentShop(null)}
        />
      )}
    </div>
  )
}

function MenuItem({ icon, label, onClick, danger }) {
  return (
    <button onClick={onClick} style={{
      width: '100%', padding: '10px 16px', background: 'none', border: 'none',
      display: 'flex', alignItems: 'center', gap: 10,
      color: danger ? '#f25d5d' : '#d1d5db', fontSize: 13, cursor: 'pointer',
      textAlign: 'left', transition: 'background .1s',
    }}
      onMouseEnter={e => e.currentTarget.style.background = danger ? 'rgba(242,93,93,0.08)' : 'rgba(255,255,255,0.05)'}
      onMouseLeave={e => e.currentTarget.style.background = 'none'}
    >
      <span style={{ fontSize: 15 }}>{icon}</span>
      <span>{label}</span>
    </button>
  )
}
